import * as React from "react"

export const Typewriter = ({
  texts,
  typingSpeed = 200,
  deletingSpeed = 100,
  delayBeforeType = 400,
  delayBeforeDelete = 800,
  loop = true,
  className,
  onComplete,
}: TypewriterProps) => {
  const spanRef = React.useRef<HTMLSpanElement | null>(null)
  const textRef = React.useRef<Text | null>(null)

  // setTimeout returns a number or undefined
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [currentIndex, setCurrentIndex] = React.useState(0)

  const isLastText = currentIndex === texts.length - 1

  const typingEffect = React.useCallback(() => {
    const chars = texts[currentIndex].split("")

    const typeLoop = () => {
      if (chars.length > 0) {
        textRef.current!.data += chars.shift()
        timer.current = setTimeout(typeLoop, typingSpeed)
      } else {
        const lastTextAndNotLooping = isLastText && !loop
        const singleTextAndLooping = texts.length === 1 && loop

        // Bypass deleting effect
        if (lastTextAndNotLooping || singleTextAndLooping) {
          onComplete?.()
          return
        }
        timer.current = setTimeout(deletingEffect, delayBeforeDelete)
      }
    }

    typeLoop()
  }, [texts, currentIndex, typingSpeed, delayBeforeDelete, loop, isLastText, onComplete])

  const deletingEffect = React.useCallback(() => {
    const chars = texts[currentIndex].split("")

    const deleteLoop = () => {
      if (chars.length > 0) {
        chars.pop()
        textRef.current!.data = chars.join("")
        timer.current = setTimeout(deleteLoop, deletingSpeed)
      } else {
        const nextIndex = (currentIndex + 1) % texts.length
        timer.current = setTimeout(() => {
          setCurrentIndex(nextIndex)
        }, delayBeforeType)
      }
    }

    deleteLoop()
  }, [texts, currentIndex, deletingSpeed, delayBeforeType])

  React.useEffect(() => {
    if (spanRef.current) {
      spanRef.current.replaceChildren((textRef.current = new Text()))
      typingEffect()

      return () => clearTimeout(timer.current)
    }
  }, [currentIndex])

  return <span ref={spanRef} className={className} />
}

type TypewriterProps = {
  texts: string[]
  typingSpeed?: number
  deletingSpeed?: number
  delayBeforeDelete?: number
  delayBeforeType?: number
  className?: string
  loop?: boolean
  onComplete?: () => void
}
